import { CalendarDays, Diamond, Flag, ListChecks, Repeat, type LucideIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ChartCard } from '@/components/patterns/ChartCard'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { cn } from '@/lib/utils'
import { agreementsHref, relationHref } from './links'
import { groupByWeek, shortDay, sundayOf } from './dates'
import { itemKey } from './itemKey'
import { plural } from './text'
import type { ItemActions } from './useItemActions'
import type { AgendaKind, DashboardItem, Scope } from './types'

type Filter = 'alles' | 'afspraken' | 'taken' | 'events'

const FILTERS: { value: Filter; label: string; match: (kind: AgendaKind) => boolean }[] = [
  { value: 'alles', label: 'Alles', match: () => true },
  { value: 'afspraken', label: 'Afspraken', match: (kind) => kind === 'deadline' || kind === 'end' },
  { value: 'taken', label: 'Taken', match: (kind) => kind === 'task' },
  { value: 'events', label: 'Events', match: (kind) => kind === 'event' },
]

function kindOf(item: DashboardItem): { Icon: LucideIcon; label: string } {
  if (item.kind === 'task') return item.schedule === 'contactritme' ? { Icon: Repeat, label: 'Ritme' } : { Icon: ListChecks, label: 'Taak' }
  if (item.kind === 'deadline') return { Icon: Diamond, label: 'Deadline' }
  if (item.kind === 'end') return { Icon: Flag, label: 'Einde' }
  return { Icon: CalendarDays, label: 'Event' }
}

/** Where a line leads: an event to its list, anything else to the relation's activity. */
function itemHref(item: DashboardItem): string | null {
  if (item.kind === 'event' && item.list_id) return `/relaties/lijsten/${item.list_id}`
  if (item.relation) return relationHref(item.relation.type, item.relation.id)
  return null
}

export function AgendaRow({ item, actions, showCheckbox }: { item: DashboardItem; actions: ItemActions; showCheckbox: boolean }) {
  const { Icon, label } = kindOf(item)
  const href = itemHref(item)
  const key = itemKey(item)
  const relationText = item.relation ? [item.relation.name, item.relation.context_name].filter(Boolean).join(' · ') : null
  const checkbox = (
    <Checkbox
      aria-label={`Markeer "${item.title}" als afgerond`}
      checked={false}
      disabled={actions.busy.has(key)}
      onChange={() => actions.complete(item)}
    />
  )
  return (
    <li className="flex items-start gap-3 py-2">
      <span className="w-16 shrink-0 text-sm tabular-nums text-muted-foreground">{shortDay(item.date)}</span>
      <span className="flex w-24 shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4 shrink-0" aria-hidden />
        {label}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        {href ? (
          <Link to={href} className="truncate text-sm font-medium underline-offset-2 hover:underline">
            {item.title}
          </Link>
        ) : (
          <span className="truncate text-sm font-medium">{item.title}</span>
        )}
        {relationText || item.detail ? (
          <span className="truncate text-sm text-muted-foreground">{[relationText, item.detail].filter(Boolean).join(' · ')}</span>
        ) : null}
      </span>
      {item.owner ? (
        <span title={item.owner.name} className="shrink-0">
          <EntityAvatar kind="person" name={item.owner.name} size="xs" />
          <span className="sr-only">{item.owner.name}</span>
        </span>
      ) : null}
      <span className="flex size-6 shrink-0 items-center justify-center">
        {showCheckbox ? actions.rhythmAnchor(item, checkbox) : null}
      </span>
    </li>
  )
}

/**
 * Widget 2: "Komende n weken" (design §4.1): tasks, deadlines (◇ also when no task exists yet), end
 * reminders and events per week, with filter chips and ticking off. `weekOnly` (set by the tile
 * "Deze week") cuts the list at Sunday. The cap of 30 and the rest ("+ n meer") come from the server.
 */
export function AgendaWidget({
  items,
  more,
  weeks,
  today,
  scope,
  weekOnly,
  onWeekOnlyChange,
  actions,
  onNewAgreement,
}: {
  items: DashboardItem[]
  more: number
  weeks: number
  today: string
  scope: Scope
  weekOnly: boolean
  onWeekOnlyChange: (value: boolean) => void
  actions: ItemActions
  onNewAgreement: () => void
}) {
  const [filter, setFilter] = useState<Filter>('alles')
  const inScope = useMemo(() => (weekOnly ? items.filter((item) => item.date <= sundayOf(today)) : items), [items, weekOnly, today])
  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((entry) => [entry.value, inScope.filter((item) => entry.match(item.kind)).length])) as Record<Filter, number>,
    [inScope],
  )
  const active = FILTERS.find((entry) => entry.value === filter) ?? FILTERS[0]
  const shown = inScope.filter((item) => active.match(item.kind))
  const groups = groupByWeek(shown, today)
  const title = `Komende ${weeks} ${weeks === 1 ? 'week' : 'weken'}`
  const listHref = agreementsHref(scope, 'sys:komend')

  return (
    <div id="agenda" className="flex h-full scroll-mt-4 flex-col">
      <ChartCard
        className="flex-1"
        title={title}
        definition="Taken, deadlines, einddata en events"
        empty={
          items.length === 0
            ? {
                title: `Niets gepland in de komende ${weeks} ${weeks === 1 ? 'week' : 'weken'}.`,
                action: (
                  <Button type="button" variant="outline" onClick={onNewAgreement}>
                    Afspraak vastleggen
                  </Button>
                ),
              }
            : null
        }
        actions={<span className="text-sm tabular-nums text-muted-foreground">{plural(inScope.length, 'item', 'items')}</span>}
      >
        <div role="group" aria-label="Filter agenda" className="flex flex-wrap items-center gap-2">
          {FILTERS.map((entry) => (
            <Button
              key={entry.value}
              type="button"
              size="sm"
              variant={filter === entry.value ? 'default' : 'outline'}
              aria-pressed={filter === entry.value}
              onClick={() => setFilter(entry.value)}
            >
              {entry.label} <span className="tabular-nums">{counts[entry.value]}</span>
            </Button>
          ))}
          <Button type="button" size="sm" variant={weekOnly ? 'default' : 'outline'} aria-pressed={weekOnly} onClick={() => onWeekOnlyChange(!weekOnly)}>
            Alleen deze week
          </Button>
        </div>
        {groups.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">Niets in deze selectie.</p>
        ) : (
          groups.map((group) => (
            <div key={group.key} className="flex flex-col">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h3>
              <ul className={cn('divide-y divide-border')}>
                {group.items.map((item) => (
                  <AgendaRow key={`${itemKey(item)}:${item.kind}`} item={item} actions={actions} showCheckbox={item.kind === 'task'} />
                ))}
              </ul>
            </div>
          ))
        )}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 text-sm">
          <Link to={agreementsHref(scope)} className="font-medium text-primary underline-offset-2 hover:underline">
            Alle afspraken →
          </Link>
          <Link to={listHref} className="font-medium text-primary underline-offset-2 hover:underline">
            {more > 0 ? `+ ${more} meer → Agenda als lijst` : 'Agenda als lijst →'}
          </Link>
        </div>
      </ChartCard>
    </div>
  )
}
